import Phaser from 'phaser';
import { ASSET_H, ASSET_W } from '../../constants';
import { getVisibleWorldBounds } from '../../hud-viewport';
import type { WeaponModel } from '../types';
import {
  clubDisplaySize,
  DEFAULT_WEAPON_TUNING,
  HAND_WHITE_TEXTURE,
  WOODEN_CLUB_TEXTURE,
} from './weapon-model';

/** Above Boogatooth (10) and geode (12). */
const HIT_DEPTH = 13;

type WindPose = { x: number; y: number; angle: number; clubW: number; clubH: number };

function varySwingMs(baseMs: number) {
  const jitter = DEFAULT_WEAPON_TUNING.swingSpeedJitter;
  const scale = 1 + (Math.random() * 2 - 1) * jitter;
  return Math.max(16, Math.round(baseMs * scale));
}

function aimPoint(hitX: number, hitY: number, model: WeaponModel) {
  return { x: hitX, y: hitY + model.aimYOffset };
}

function baseGrip(model: WeaponModel) {
  return { x: model.gripX, y: model.gripY };
}

function clubReach(model: WeaponModel) {
  return model.displayHeight * DEFAULT_WEAPON_TUNING.clubOriginY;
}

function farSwingFactor(hitX: number, hitY: number, model: WeaponModel) {
  const base = baseGrip(model);
  const strikeReach = clubReach(model);
  const dist = Phaser.Math.Distance.Between(base.x, base.y, hitX, hitY);
  return Phaser.Math.Clamp((dist - strikeReach * 0.75) / 550, 0, 1);
}

function swingGrip(hitX: number, hitY: number, model: WeaponModel) {
  const base = baseGrip(model);
  const strikeReach = clubReach(model);
  const dx = hitX - base.x;
  const dy = hitY - base.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= strikeReach) return base;
  const pull = (dist - strikeReach) / dist;
  return { x: base.x + dx * pull, y: base.y + dy * pull };
}

function clubExtents(angleDeg: number, model: WeaponModel) {
  const rad = Phaser.Math.DegToRad(angleDeg);
  const len = clubReach(model);
  const { width: clubW } = clubDisplaySize(model);
  const hw = clubW * 0.5;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  let minX = 0;
  let maxX = 0;
  for (const lx of [-hw, hw]) {
    for (const ly of [0, -len]) {
      const wx = lx * cos - ly * sin;
      minX = Math.min(minX, wx);
      maxX = Math.max(maxX, wx);
    }
  }
  const width = maxX - minX;
  return { left: -minX, right: maxX, width };
}

function windStartX(windAngleDeg: number, farFactor: number, model: WeaponModel) {
  const { right, width } = clubExtents(windAngleDeg, model);
  const panelStart = ASSET_W - right + width * (1 - DEFAULT_WEAPON_TUNING.startOffscreenRatio);
  return panelStart + width * DEFAULT_WEAPON_TUNING.farStartPush * farFactor;
}

function aimAtClubHead(
  gripX: number,
  gripY: number,
  hitX: number,
  hitY: number,
  useDirectAim: boolean,
  model: WeaponModel,
) {
  if (useDirectAim) return { x: hitX, y: hitY };
  const dx = hitX - gripX;
  const dy = hitY - gripY;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) return { x: hitX, y: hitY };
  const strikeReach = clubReach(model);
  const inset = Math.min(strikeReach * DEFAULT_WEAPON_TUNING.hitEdgeInsetRatio, dist * 0.9);
  const t = inset / dist;
  return { x: hitX - dx * t, y: hitY - dy * t };
}

function clubAngleDeg(
  gripX: number,
  gripY: number,
  hitX: number,
  hitY: number,
  useDirectAim: boolean,
  model: WeaponModel,
) {
  const aim = aimAtClubHead(gripX, gripY, hitX, hitY, useDirectAim, model);
  const dx = aim.x - gripX;
  return Phaser.Math.RadToDeg(Math.atan2(dx, gripY - aim.y));
}

/** Default aim toward geode center (matches world-model layout). */
function defaultIdleAim(model: WeaponModel) {
  const cx = ASSET_W * 0.76;
  const cy = ASSET_H * 0.7;
  return aimPoint(cx, cy, model);
}

function clubWorldBounds(gripX: number, gripY: number, angleDeg: number, model: WeaponModel) {
  const rad = Phaser.Math.DegToRad(angleDeg);
  const len = clubReach(model);
  const { width: clubW, height: clubH } = clubDisplaySize(model);
  const hw = clubW * 0.5;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const tail = clubH * (1 - DEFAULT_WEAPON_TUNING.clubOriginY);

  let minX = 0;
  let maxX = 0;
  let minY = 0;
  let maxY = tail;

  for (const lx of [-hw, hw]) {
    for (const ly of [0, -len, tail]) {
      const wx = lx * cos - ly * sin;
      const wy = lx * sin + ly * cos;
      minX = Math.min(minX, wx);
      maxX = Math.max(maxX, wx);
      minY = Math.min(minY, wy);
      maxY = Math.max(maxY, wy);
    }
  }

  return {
    left: gripX + minX,
    right: gripX + maxX,
    top: gripY + minY,
    bottom: gripY + maxY,
  };
}

function clubLocalExtents(angleDeg: number, model: WeaponModel) {
  const b = clubWorldBounds(0, 0, angleDeg, model);
  return { minX: b.left, maxX: b.right, minY: b.top, maxY: b.bottom };
}

function fitIdleGrip(
  gripY: number,
  angleDeg: number,
  model: WeaponModel,
  scene: Phaser.Scene,
) {
  const visible = getVisibleWorldBounds(scene);
  const fraction = DEFAULT_WEAPON_TUNING.idleVisibleFraction;
  const pad = DEFAULT_WEAPON_TUNING.idleVisiblePadding;
  const local = clubLocalExtents(angleDeg, model);
  const clubW = local.maxX - local.minX;

  // `fraction` of club width inside the visible crop; remainder hangs off the right.
  let x = visible.right - pad - clubW * fraction - local.minX;
  let y = gripY;

  for (let pass = 0; pass < 4; pass++) {
    const bounds = clubWorldBounds(x, y, angleDeg, model);
    if (bounds.left < visible.left + pad) x += visible.left + pad - bounds.left;
    if (bounds.bottom > visible.bottom - pad) y -= bounds.bottom - (visible.bottom - pad);
    if (bounds.top < visible.top + pad) y += visible.top + pad - bounds.top;
  }

  return { x, y };
}

function computeWindPose(model: WeaponModel, hitX: number, hitY: number, farFactor = 0): WindPose {
  const { width: clubW, height: clubH } = clubDisplaySize(model);
  const aim = aimPoint(hitX, hitY, model);
  const grip = baseGrip(model);
  const aimAngle = clubAngleDeg(grip.x, grip.y, aim.x, aim.y, false, model);
  const windAngle = aimAngle + model.windDeg + DEFAULT_WEAPON_TUNING.windDegFarExtra * farFactor;
  const startX = windStartX(windAngle, farFactor, model);
  return { x: startX, y: grip.y, angle: windAngle, clubW, clubH };
}

/** Idle wind pose — fraction of club width on screen, adapted to FIT/ENVELOP crop. */
function computeIdlePose(scene: Phaser.Scene, model: WeaponModel, hitX: number, hitY: number): WindPose {
  const pose = computeWindPose(model, hitX, hitY);
  const fitted = fitIdleGrip(pose.y, pose.angle, model, scene);
  return { ...pose, x: fitted.x, y: fitted.y };
}

function layoutSwingLayers(
  club: Phaser.GameObjects.Image,
  hand: Phaser.GameObjects.Image,
  clubW: number,
  clubH: number,
) {
  const originX = DEFAULT_WEAPON_TUNING.clubOriginX;
  const originY = DEFAULT_WEAPON_TUNING.clubOriginY;
  for (const layer of [club, hand]) {
    layer
      .setOrigin(originX, originY)
      .setPosition(0, 0)
      .setDisplaySize(Math.round(clubW), Math.round(clubH));
  }
}

export function createWeaponHitView(scene: Phaser.Scene, getModel: () => WeaponModel) {
  const root = scene.add.container(0, 0).setDepth(HIT_DEPTH).setVisible(false);
  const club = scene.add.image(0, 0, WOODEN_CLUB_TEXTURE);
  const hand = scene.add.image(0, 0, HAND_WHITE_TEXTURE);
  root.add([club, hand]);

  let idleWanted = false;
  let swinging = false;

  /** Fixed rest pose — always geode-center aim + screen-fitted slot. */
  function resolveDefaultIdlePose(model: WeaponModel) {
    const aim = defaultIdleAim(model);
    return computeIdlePose(scene, model, aim.x, aim.y);
  }

  function texturesReady() {
    return scene.textures.exists(WOODEN_CLUB_TEXTURE) && scene.textures.exists(HAND_WHITE_TEXTURE);
  }

  function stopMotion() {
    scene.tweens.killTweensOf(root);
  }

  function applyPose(pose: WindPose) {
    layoutSwingLayers(club, hand, pose.clubW, pose.clubH);
    root.setPosition(pose.x, pose.y).setAngle(pose.angle).setAlpha(1);
  }

  function idleHoverMotion(pose: WindPose) {
    const model = getModel();
    const visible = getVisibleWorldBounds(scene);
    const pad = DEFAULT_WEAPON_TUNING.idleVisiblePadding;
    const base = clubWorldBounds(pose.x, pose.y, pose.angle, model);
    const yDownRoom = visible.bottom - pad - base.bottom;
    const yUpRoom = base.top - (visible.top + pad);
    const yAmp = Math.min(
      DEFAULT_WEAPON_TUNING.idleHoverY,
      Math.max(0, yDownRoom),
      Math.max(0, yUpRoom),
    );

    const angleProbe = DEFAULT_WEAPON_TUNING.idleHoverAngle;
    let angleAmp: number = angleProbe;
    for (const sign of [1, -1]) {
      const bounds = clubWorldBounds(pose.x, pose.y, pose.angle + sign * angleProbe, getModel());
      if (
        bounds.left < visible.left + pad ||
        bounds.right > visible.right - pad ||
        bounds.top < visible.top + pad ||
        bounds.bottom > visible.bottom - pad
      ) {
        angleAmp = Math.min(angleAmp, angleProbe * 0.45);
      }
    }

    return { yAmp, angleAmp };
  }

  function startIdleHover(pose: WindPose) {
    stopMotion();
    applyPose(pose);
    root.setVisible(true);

    const { yAmp, angleAmp } = idleHoverMotion(pose);
    const { idleHoverDurationMs } = DEFAULT_WEAPON_TUNING;

    if (yAmp > 0.5) {
      scene.tweens.add({
        targets: root,
        y: pose.y - yAmp,
        duration: idleHoverDurationMs,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }

    if (angleAmp > 0.2) {
      scene.tweens.add({
        targets: root,
        angle: pose.angle + angleAmp,
        duration: Math.round(idleHoverDurationMs * 1.15),
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  function showIdle() {
    if (!texturesReady()) return;
    idleWanted = true;
    if (swinging) return;

    const model = getModel();
    startIdleHover(resolveDefaultIdlePose(model));
  }

  function recoverToIdlePose() {
    swinging = false;
    if (!idleWanted || !texturesReady()) {
      root.setVisible(false);
      return;
    }

    const model = getModel();
    const pose = resolveDefaultIdlePose(model);
    stopMotion();
    layoutSwingLayers(club, hand, pose.clubW, pose.clubH);

    scene.tweens.add({
      targets: root,
      x: pose.x,
      y: pose.y,
      angle: pose.angle,
      alpha: 1,
      duration: varySwingMs(
        DEFAULT_WEAPON_TUNING.swingOutMs + DEFAULT_WEAPON_TUNING.idleReturnMs,
      ),
      ease: 'Sine.easeOut',
      onComplete: () => startIdleHover(pose),
    });
  }

  function hide() {
    idleWanted = false;
    swinging = false;
    stopMotion();
    root.setVisible(false);
  }

  function playWoodClubSwing(hitX: number, hitY: number) {
    if (!texturesReady()) return;

    const model = getModel();
    const { width: clubW, height: clubH } = clubDisplaySize(model);
    const strikeReach = clubReach(model);

    idleWanted = true;
    const fromIdle = root.visible;
    swinging = true;
    stopMotion();

    const aim = aimPoint(hitX, hitY, model);

    const base = baseGrip(model);
    const farFactor = farSwingFactor(aim.x, aim.y, model);
    const baseDist = Phaser.Math.Distance.Between(base.x, base.y, aim.x, aim.y);
    const grip = swingGrip(aim.x, aim.y, model);
    const useDirectAim = baseDist > strikeReach * 0.9;
    const aimAngle = clubAngleDeg(grip.x, grip.y, aim.x, aim.y, useDirectAim, model);
    const windAngle = aimAngle + model.windDeg + DEFAULT_WEAPON_TUNING.windDegFarExtra * farFactor;

    layoutSwingLayers(club, hand, clubW, clubH);

    // Start from idle pose when visible — swing follows the tap from where the club already is.
    const startX = fromIdle ? root.x : windStartX(windAngle, farFactor, model);
    const startY = fromIdle ? root.y : grip.y;
    const startAngle = fromIdle ? root.angle : windAngle;

    root
      .setPosition(startX, startY)
      .setAngle(startAngle)
      .setAlpha(1)
      .setVisible(true);

    scene.tweens.add({
      targets: root,
      x: grip.x,
      y: grip.y,
      angle: aimAngle,
      duration: varySwingMs(
        DEFAULT_WEAPON_TUNING.swingInMs + Math.round(25 * farFactor),
      ),
      ease: 'Quad.easeIn',
      onComplete: () => recoverToIdlePose(),
    });
  }

  function playHit(weapon: string | null | undefined, hitX: number, hitY: number) {
    if (weapon !== 'WoodClub' && weapon !== 'Wood Club') return;
    playWoodClubSwing(hitX, hitY);
  }

  function refreshIdle() {
    if (idleWanted && !swinging) showIdle();
  }

  return {
    playHit,
    showIdle,
    hide,
    refreshIdle,
    destroy() {
      hide();
      root.destroy();
    },
  };
}
